(() => {
  'use strict';
  if (window.collectionBrowseStateReady) return;
  window.collectionBrowseStateReady = true;

  const STATE_KEY = '13de:collection-browse:state:v1';
  const SNAPSHOT_KEY = '13de:collection-browse:snapshot:v1';
  const STATE_TTL = 12 * 60 * 60 * 1000;
  const SNAPSHOT_TTL = 10 * 60 * 1000;
  const MAX_SNAPSHOT_LENGTH = 1200000;
  const read = key => {
    try { return JSON.parse(sessionStorage.getItem(key) || 'null'); }
    catch (_) { return null; }
  };
  const viewKey = root => {
    const url = new URL(location.href);
    url.searchParams.sort();
    return [window.Shopify?.theme?.id || '', root.dataset.section || '',
      document.documentElement.lang, window.Shopify?.country || '',
      window.Shopify?.currency?.active || '', url.pathname, url.search].join('|');
  };
  const isHistoryReturn = () => {
    const navigation = performance.getEntriesByType?.('navigation')?.[0];
    return navigation ? navigation.type === 'back_forward' : performance.navigation?.type === 2;
  };

  function mount(root) {
    if (root.dataset.saBrowseBound || !root.querySelector('#product-grid')) return;
    root.dataset.saBrowseBound = 'true';
    let restoring = false;

    function save(link) {
      if (restoring || !root.isConnected) return;
      const grid = root.querySelector('#product-grid');
      const more = root.querySelector('[data-sa-more]');
      const cards = Array.from(grid?.querySelectorAll('[data-sa-product]') || []);
      if (!cards.length || !more) return;
      const current = new URL(location.href);
      const firstPage = Number(current.searchParams.get('page') || 1);
      const lastPage = Number(more.dataset.saPage || firstPage);
      if (!Number.isInteger(firstPage) || !Number.isInteger(lastPage) || lastPage < firstPage) return;
      const clicked = link?.closest('[data-sa-product]');
      const anchor = clicked || cards.find(card => {
        const box = card.getBoundingClientRect();
        return box.bottom > 0 && box.top < window.innerHeight;
      }) || cards[0];
      const stamp = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const state = { key: viewKey(root), stamp, savedAt: Date.now(), firstPage, lastPage,
        total: more.dataset.saTotal || '', count: cards.length, y: window.scrollY,
        anchor: anchor.dataset.saProduct, offset: anchor.getBoundingClientRect().top };
      try {
        // A single bounded snapshot keeps long collection browsing within mobile storage limits.
        const cleanMore = more.cloneNode(true);
        const next = cleanMore.querySelector('[data-sa-next]');
        if (next?.getAttribute('aria-disabled') === 'true') {
          next.removeAttribute('aria-disabled');
          next.textContent = next.dataset.label || 'Discover more';
        }
        cleanMore.querySelector('[data-sa-more-error]')?.setAttribute('hidden', '');
        const snapshot = { key: state.key, stamp, savedAt: state.savedAt,
          grid: grid.innerHTML, more: cleanMore.outerHTML };
        const json = JSON.stringify(snapshot);
        if (json.length <= MAX_SNAPSHOT_LENGTH) {
          try { sessionStorage.setItem(SNAPSHOT_KEY, json); }
          catch (_) { sessionStorage.removeItem(SNAPSHOT_KEY); }
        } else sessionStorage.removeItem(SNAPSHOT_KEY);
        const previous = read(STATE_KEY);
        const states = (Array.isArray(previous) ? previous : [])
          .filter(item => item.key !== state.key && Date.now() - item.savedAt < STATE_TTL);
        sessionStorage.setItem(STATE_KEY, JSON.stringify([state, ...states].slice(0, 8)));
      } catch (_) {
        // Browsing remains available when a browser disallows session storage.
      }
    }

    async function restore() {
      if (!isHistoryReturn()) return;
      const states = read(STATE_KEY);
      const state = Array.isArray(states) && states.find(item => item.key === viewKey(root));
      if (!state || Date.now() - state.savedAt > STATE_TTL ||
          !Number.isInteger(state.firstPage) || !Number.isInteger(state.lastPage) ||
          state.lastPage < state.firstPage || state.lastPage - state.firstPage > 100 ||
          !Number.isFinite(state.y) || !Number.isFinite(state.offset)) return;
      const grid = root.querySelector('#product-grid');
      const container = root.querySelector('#ProductGridContainer');
      let more = root.querySelector('[data-sa-more]');
      if (!grid || !more) return;
      const abort = new AbortController();
      let cancelled = false;
      const cancel = () => { cancelled = true; abort.abort(); };
      for (const name of ['pointerdown', 'touchstart', 'wheel', 'keydown'])
        window.addEventListener(name, cancel, { once: true, passive: true });
      const previousScrollMode = history.scrollRestoration;
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      restoring = true;
      container?.setAttribute('aria-busy', 'true');

      try {
        const snapshot = read(SNAPSHOT_KEY);
        let cached = false;
        if (snapshot?.key === state.key && snapshot.stamp === state.stamp &&
            Date.now() - snapshot.savedAt < SNAPSHOT_TTL &&
            state.total === (more.dataset.saTotal || '')) {
          const restoredGrid = grid.cloneNode(false);
          restoredGrid.innerHTML = snapshot.grid;
          const cards = Array.from(restoredGrid.querySelectorAll('[data-sa-product]'));
          const template = document.createElement('template');
          template.innerHTML = snapshot.more;
          const restoredMore = template.content.firstElementChild;
          if (cards.length === state.count &&
              new Set(cards.map(card => card.dataset.saProduct)).size === cards.length &&
              restoredMore?.hasAttribute('data-sa-more')) {
            grid.replaceChildren(...restoredGrid.childNodes);
            more.replaceWith(restoredMore);
            more = restoredMore;
            cached = true;
          }
        }

        if (!cached && state.lastPage > state.firstPage) {
          // If the snapshot expired or exceeded storage limits, rebuild from fresh section pages.
          const totalPages = Number(more.dataset.saPages || state.lastPage);
          const lastPage = Math.min(state.lastPage, totalPages);
          const pages = [];
          for (let page = state.firstPage + 1; page <= lastPage; page++) pages.push(page);
          const existing = new Set(Array.from(grid.children).map(card => card.dataset.saProduct));
          for (let index = 0; index < pages.length; index += 3) {
            const responses = await Promise.all(pages.slice(index, index + 3).map(async page => {
              const url = new URL(location.href);
              url.searchParams.set('page', String(page));
              url.searchParams.set('section_id', root.dataset.section);
              const response = await fetch(url, { signal: abort.signal, credentials: 'same-origin' });
              if (!response.ok) throw Error('Unable to restore collection');
              const html = new DOMParser().parseFromString(await response.text(), 'text/html');
              const nextGrid = html.querySelector('#product-grid');
              const nextMore = html.querySelector('[data-sa-more]');
              if (!nextGrid || !nextMore) throw Error('Incomplete collection page');
              return { grid: nextGrid, more: nextMore };
            }));
            if (cancelled || viewKey(root) !== state.key || !grid.isConnected) return;
            for (const response of responses) {
              for (const card of Array.from(response.grid.children)) {
                if (!existing.has(card.dataset.saProduct)) {
                  existing.add(card.dataset.saProduct);
                  grid.append(card);
                }
              }
              more.replaceWith(response.more);
              more = response.more;
            }
          }
        }
        if (cancelled || viewKey(root) !== state.key) return;
        grid.querySelectorAll('.scroll-trigger').forEach(card => card.classList.add('scroll-trigger--cancel'));
        const position = () => {
          if (cancelled || !grid.isConnected) return;
          const anchor = Array.from(grid.querySelectorAll('[data-sa-product]'))
            .find(card => card.dataset.saProduct === state.anchor);
          const y = anchor ? window.scrollY + anchor.getBoundingClientRect().top - state.offset : state.y;
          window.scrollTo(0, Math.max(0, y));
        };
        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        position();
        // Recheck after nearby images settle; a user gesture always cancels automatic positioning.
        const anchor = Array.from(grid.querySelectorAll('[data-sa-product]'))
          .find(card => card.dataset.saProduct === state.anchor);
        const images = Array.from(anchor?.querySelectorAll('img') || []).filter(image => !image.complete);
        if (images.length) {
          await Promise.race([
            Promise.allSettled(images.map(image => image.decode?.())),
            new Promise(resolve => setTimeout(resolve, 400))
          ]);
          position();
        }
        const announcement = root.querySelector('[data-sa-announcement]');
        if (announcement && !cancelled)
          announcement.textContent = document.documentElement.lang.startsWith('zh')
            ? '已恢复之前浏览的商品和位置' : 'Your previous browsing position has been restored';
      } catch (_) {
        // Preserve the working first page and Discover more link if a restore request fails.
      } finally {
        restoring = false;
        container?.removeAttribute('aria-busy');
        for (const name of ['pointerdown', 'touchstart', 'wheel', 'keydown'])
          window.removeEventListener(name, cancel);
        if ('scrollRestoration' in history) history.scrollRestoration = previousScrollMode;
      }
    }

    root.addEventListener('click', event => {
      const link = event.target.closest?.('a[href]');
      if (!link || !link.closest('#product-grid')) return;
      const url = new URL(link.href, location.origin);
      if (url.origin === location.origin && url.pathname.includes('/products/')) save(link);
    }, true);
    window.addEventListener('pagehide', () => save());
    // BFCache returns keep their original DOM and native scroll position; only rebuilt documents replay.
    restore();
  }

  const boot = () => document.querySelectorAll('[data-sa-shop]').forEach(mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
